import { CdkListboxModule } from '@angular/cdk/listbox';
import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  signal,
  ViewChild,
  AfterViewInit,
  inject,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CorpStructNode } from '../../../../models/api/alerts/corp-struct-node';
import { Action } from '../../../../models/core/action';
import { Api } from '../../../../services/api/api.service';
import { ButtonComponent } from '../../buttons/button.component';
import { ActionRowComponent } from '../../panels/action-row.component';
import { DialogComponent } from '../../panels/dialog.component';
import { LoadingPanelComponent } from '../../panels/loading-panel.component';
import { MenuComponent } from '../../panels/menu.component';
import { TreeComponent, TreeNode } from '../../tree/tree.component';
import { BaseControlComponent } from '../base-control.component';
import { CheckboxComponent } from '../checkbox.component';
import { HelpTextComponent } from '../help-text.component';
import { Operator, OperatorInputComponent } from '../operator-input.component';
import { RadioButtonComponent } from '../radio-button.component';
import { CorporateStructureComponent } from './corp-structs.component';

@Component({
  selector: 'app-corp-struct-selector',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    HelpTextComponent,
    ButtonComponent,
    ActionRowComponent,
    RadioButtonComponent,
    CorporateStructureComponent,
    DialogComponent,
    CdkListboxModule,
    CheckboxComponent,
    MenuComponent,
    TreeComponent,
    OperatorInputComponent,
    LoadingPanelComponent,
  ],
  template: `
    <app-action-row>
      <div left1>
        <app-button
          (click)="selectMenu.toggle($event)"
          emphasis="Low"
          icon="fa-light fa-ellipsis-vertical"
          label="Add corporate structure"
        />
      </div>
      <div right1 class="tw-flex tw-items-center">
        <app-radio-button
          [control]="include"
          optionLabel="name"
          optionValue="code"
          [options]="includeOps"
          [showHelpText]="false"
          [vertical]="false"
        ></app-radio-button>
      </div>
    </app-action-row>
    <app-corp-structs
      [items]="selectedItems"
      (itemsChanged)="onSelectedItemsChanged($event)"
    />
    @if (showHelpText) {
      <app-help-text [control]="control" [helpText]="helpText" />
    }
    <app-dialog
      #searchCoporateStructDialog
      [closable]="false"
      label="Search corporate structure"
      width="60rem"
    >
      <ng-template #content>
        <form [formGroup]="fg" class="tw-py-2">
          <div
            class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-items-center"
          >
            <div class="tw-w-full sm:tw-basis-1/2">
              <app-operator-input
                [control]="fg.controls.name"
                [operatorControl]="fg.controls.nameOp"
                [operators]="nameOperators"
                label="Name"
                [showHelpText]="false"
              />
            </div>
            <app-button
              emphasis="Low"
              left1
              label="Search"
              type="submit"
              [loading]="searching"
              (click)="search()"
            />
            <div class="tw-grow tw-flex tw-flex-row-reverse">
              <app-checkbox
                [control]="fg.controls.includeIntermediate"
                label="Include intermediate levels"
                [showHelpText]="false"
              />
            </div>
          </div>
        </form>

        <div class="tw-pb-8 tw-pt-4">
          <app-loading-panel
            loadingStyle="Box"
            [state]="searching ? 'Loading' : 'Loaded'"
          >
            <ng-template #loaded>
              <app-corp-structs
                #structs
                [items]="searchItems"
                paginatorDropdownAppendTo="self"
                (selected)="onSearchSelectionChanged($event)"
                [showDeleteColumn]="false"
              />
            </ng-template>
          </app-loading-panel>
        </div>
        <app-action-row>
          <app-button
            [disabled]="!searchSelectedCount"
            [badgeLabel]="searchSelectedCount ? searchSelectedCount : null"
            right1
            label="Add"
            (click)="addFromSearch(searchCoporateStructDialog)"
          />
          <app-button
            right2
            emphasis="Low"
            label="Close"
            (click)="closeDialog(searchCoporateStructDialog)"
          />
        </app-action-row>
      </ng-template>
    </app-dialog>
    <app-dialog
      #browseCoporateStructDialog
      [closable]="false"
      label="Browse corporate structures"
    >
      <ng-template #content>
        <app-loading-panel
          loadingStyle="Box"
          [state]="loadingTree() ? 'Loading' : 'Loaded'"
        >
          <ng-template #loaded>
            <app-tree
              [dataSource]="nodeItems()"
              (select)="onSeletedNodes($event)"
              (unSelect)="onSeletedNodes($event)"
              (expand)="onExpandNode($event)"
            ></app-tree>
          </ng-template>
        </app-loading-panel>
        <app-action-row useMobileLayout="true">
          <app-button
            [disabled]="!browseSelectedCount"
            [badgeLabel]="browseSelectedCount ? browseSelectedCount : null"
            right1
            label="Add"
            (click)="addFromBrowse(browseCoporateStructDialog)"
          />
          <app-button
            right2
            emphasis="Low"
            label="Close"
            (click)="closeDialog(browseCoporateStructDialog)"
          />
        </app-action-row>
      </ng-template>
    </app-dialog>
    <app-menu
      [items]="actions"
      #selectMenu
      (click)="openSelectedDialog($event)"
    ></app-menu>
  `,
  styles: ``,
})
export class CorpStructSelectorComponent
  extends BaseControlComponent
  implements OnInit, AfterViewInit
{
  @Input() contractNumber = 0;

  include = new FormControl<string>('Include');

  @ViewChild('searchCoporateStructDialog')
  searchCoporateStructDialog: DialogComponent;
  @ViewChild('browseCoporateStructDialog')
  browseCoporateStructDialog: DialogComponent;
  @ViewChild('structs')
  structs: CorporateStructureComponent;
  @ViewChild(TreeComponent)
  treeComponent: TreeComponent;

  nameOperators: Operator[] = [
    { key: 'eq', placeholder: 'Name or number', label: 'Equals' },
    {
      key: 'contains',
      placeholder: 'Name or number contains',
      label: 'Contains',
    },
    {
      key: 'sw',
      placeholder: 'Name or number starts with',
      label: 'Starts with',
    },
    { key: 'ew', placeholder: 'Name or number ends with', label: 'Ends with' },
  ];

  actions: Action[] = [];
  nodeItems = signal<TreeNode[]>([]);
  searchItems: CorpStruct[] = [];
  selectedItems: CorpStruct[] = [];

  searching = false;
  loadingTree = signal<boolean>(false);

  searchSelectedItems: CorpStruct[] = [];
  browseSelectedNodes: TreeNode[] = [];
  private nodeDataMap = new Map<number, CorpStructNode>();

  get searchSelectedCount(): number {
    return this.searchSelectedItems?.length || 0;
  }

  get browseSelectedCount(): number {
    return this.getTopLevelSelectedNodes().length;
  }

  private api: Api;

  constructor() {
    const api = inject(Api);

    super();
    this.api = api;
  }

  ngOnInit() {
    if (this.control?.value) {
      const value = this.control.value as {
        includeType: string;
        nodes: CorpStruct[];
      };

      if (value.includeType) {
        this.include.setValue(value.includeType);
      }

      if (value.nodes?.length > 0) {
        this.selectedItems = [...value.nodes];
      }
    }

    this.include.valueChanges.subscribe(() => {
      this.updateControlValue();
    });
  }

  ngAfterViewInit() {
    this.actions = [
      { label: 'Search', dialog: this.searchCoporateStructDialog },
      { label: 'Browse', dialog: this.browseCoporateStructDialog },
    ];
  }

  includeOps = [
    { name: 'Include', code: 'Include' },
    { name: 'Exclude', code: 'Exclude' },
  ];

  fg = new FormGroup({
    name: new FormControl<string>(null),
    nameOp: new FormControl<string>('contains'),
    includeIntermediate: new FormControl<boolean>(false),
  });

  onSearchSelectionChanged(event: CorpStruct[]) {
    this.searchSelectedItems = event || [];
  }

  onSeletedNodes(event: TreeNode | TreeNode[]) {
    this.browseSelectedNodes = Array.isArray(event)
      ? event
      : event
        ? [event]
        : [];
  }

  async onExpandNode(node: TreeNode) {
    if (node.loaded || this.contractNumber <= 0) {
      return;
    }

    try {
      const response = await this.api.newAlert.getCorpStruct({
        contractNumber: this.contractNumber,
        parentNodeId: node.value as number,
      });

      const children = response.nodes.map((n) => this.mapToTreeNode(n, node));

      node.children = children;
      node.loaded = true;
      node.loading = false;

      // If parent is selected, auto-select all loaded children
      if (this.treeComponent) {
        const isParentSelected = this.treeComponent.selectedNodes.some(
          (n) => n.value === node.value,
        );

        if (isParentSelected) {
          for (const child of children) {
            if (!this.treeComponent.selectedNodes.includes(child)) {
              this.treeComponent.selectedNodes.push(child);
            }
          }

          this.treeComponent.selectedNodes = [
            ...this.treeComponent.selectedNodes,
          ];
          this.onSeletedNodes(this.treeComponent.selectedNodes);
        }
      }

      // Trigger tree update via signal
      this.nodeItems.set([...this.nodeItems()]);
    } catch {
      node.loaded = true;
      node.loading = false;
      this.nodeItems.set([...this.nodeItems()]);
    }
  }

  addFromSearch(dialog: DialogComponent) {
    if (this.searchSelectedItems.length === 0) {
      return;
    }

    const existingIds = new Set(this.selectedItems.map((i) => i.corpStructId));

    for (const item of this.searchSelectedItems) {
      if (!existingIds.has(item.corpStructId)) {
        this.selectedItems.push({ ...item });
        existingIds.add(item.corpStructId);
      }
    }

    this.selectedItems = [...this.selectedItems];
    this.searchSelectedItems = [];
    this.updateControlValue();
    dialog.visible = false;
  }

  addFromBrowse(dialog: DialogComponent) {
    const topLevelNodes = this.getTopLevelSelectedNodes();
    if (topLevelNodes.length === 0) {
      return;
    }

    const existingIds = new Set(this.selectedItems.map((i) => i.corpStructId));

    for (const node of topLevelNodes) {
      const corpStruct = this.mapTreeNodeToCorpStruct(node);
      if (!existingIds.has(corpStruct.corpStructId)) {
        this.selectedItems.push(corpStruct);
        existingIds.add(corpStruct.corpStructId);
      }
    }

    this.selectedItems = [...this.selectedItems];
    this.browseSelectedNodes = [];
    this.updateControlValue();
    dialog.visible = false;
  }

  closeDialog(dialog: DialogComponent) {
    if (dialog === this.browseCoporateStructDialog) {
      this.browseSelectedNodes = [];
    } else if (dialog === this.searchCoporateStructDialog) {
      this.searchSelectedItems = [];
    }

    dialog.visible = false;
  }

  async search() {
    if (this.contractNumber <= 0) {
      return;
    }

    this.searching = true;
    this.searchSelectedItems = [];

    try {
      const response = await this.api.newAlert.searchCorpStruct({
        contractNumber: this.contractNumber,
        searchText: this.fg.controls.name.value || '',
        searchOperator: this.fg.controls.nameOp.value || 'contains',
        includeIntermediateLevels:
          this.fg.controls.includeIntermediate.value || false,
      });

      this.searchItems = response.nodes.map((n) => this.mapToCorpStruct(n));
    } catch {
      this.searchItems = [];
    } finally {
      this.searching = false;
    }
  }

  async openSelectedDialog(event: Action) {
    if (event.label === 'Browse') {
      this.browseSelectedNodes = [];
      if (this.treeComponent) {
        this.treeComponent.selectedNodes = [];
      }

      await this.loadRootNodes();
    } else if (event.label === 'Search') {
      this.searchSelectedItems = [];
      this.searchItems = [];
    }

    if (event.dialog) {
      event.dialog.visible = true;
    }
  }

  async loadRootNodes() {
    if (this.contractNumber <= 0) {
      return;
    }

    this.loadingTree.set(true);

    try {
      const response = await this.api.newAlert.getCorpStruct({
        contractNumber: this.contractNumber,
        parentNodeId: 0,
      });

      this.nodeItems.set(
        response.nodes.map((n) => this.mapToTreeNode(n, null)),
      );
    } catch {
      this.nodeItems.set([]);
    } finally {
      this.loadingTree.set(false);
    }
  }

  onSelectedItemsChanged(items: CorpStruct[]) {
    this.selectedItems = items;
    this.updateControlValue();
  }

  private getTopLevelSelectedNodes(): TreeNode[] {
    if (!this.browseSelectedNodes?.length) {
      return [];
    }

    const selectedValues = new Set(
      this.browseSelectedNodes.map((n) => n.value),
    );

    return this.browseSelectedNodes.filter(
      (node) => !node.parent || !selectedValues.has(node.parent.value),
    );
  }

  private mapToCorpStruct(node: CorpStructNode): CorpStruct {
    return {
      corpStructId: `node-${node.nodeId}`,
      name: node.locationName || '',
      accountNumber: node.accountNumber ?? null,
      unitNumber: node.unitNumber || null,
      code: node.locCode || null,
      node: node.nodeId,
    };
  }

  private mapToTreeNode(
    node: CorpStructNode,
    parent: TreeNode | null,
  ): TreeNode {
    this.nodeDataMap.set(node.nodeId, node);

    const treeNode = new TreeNode();
    treeNode.label = node.locationName || '';
    treeNode.value = node.nodeId;
    treeNode.checked = false;
    treeNode.selected = false;
    treeNode.loaded = !node.hasChildren;
    treeNode.parent = parent;
    treeNode.children = node.hasChildren ? undefined : [];
    return treeNode;
  }

  private mapTreeNodeToCorpStruct(node: TreeNode): CorpStruct {
    const nodeId = node.value as number;
    const data = this.nodeDataMap.get(nodeId);
    return {
      corpStructId: `node-${nodeId}`,
      name: node.label,
      accountNumber: data?.accountNumber ?? null,
      unitNumber: data?.unitNumber || null,
      code: data?.locCode || null,
      node: nodeId,
    };
  }

  private updateControlValue() {
    if (this.control) {
      this.control.setValue({
        includeType: this.include.value,
        nodes: this.selectedItems,
      });
    }
  }
}

export class CorpStruct {
  corpStructId: string;
  name: string;
  accountNumber: number;
  unitNumber: string;
  code: string;
  node: number;
}
